/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React from 'react';
var utils = require('../../../Utils.js');

const SensitivityRates = props => {
  if (props.values.therapySetting !== 'initial') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Insulin Sensitivity Factor</div>
        {props.values.sensitivityArray &&
          props.values.sensitivityArray.map((range, index) => (
            <div key={index} variant="subtitle1" color="textSecondary">
              {utils.fixTime(range.time)} : {range.val} mg/dL per U
            </div>
          ))}
        <br />
      </div>
    );
  }
};

const InsulinToCarbRatios = props => {
  if (props.values.therapySetting !== 'initial') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Insulin to Carb Ratios:</div>
        {props.values.bolusArray &&
          props.values.bolusArray.map((range, index) => (
            <div key={index} variant="subtitle1" color="textSecondary">
              {utils.fixTime(range.time)} : {range.val} g/U
            </div>
          ))}
        <br />
      </div>
    );
  }
};

const BasalRates = props => {
  if (props.values.therapySetting !== 'initial') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Basal Rates:</div>
        {props.values.basalArray &&
          props.values.basalArray.map((range, index) => (
            <div key={index} variant="subtitle1" color="textSecondary">
              {utils.fixTime(range.time)} : {range.val} U/hr
            </div>
          ))}
        <br />
      </div>
    );
  }
};

const MaxBolus = props => {
  if (props.values.therapySetting !== 'initial') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Max Bolus: {props.values.maxBolus} U</div>
        <br />
      </div>
    );
  }
};

const MaxBasal = props => {
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else {
    if (props.values.loopMode === 'som') {
      return (
        <div>
          <div>Max Basal: 100%</div>
          <br />
        </div>
      );
    } else if (props.values.loopMode === 'clm') {
      return (
        <div>
          <div>Max Basal: {props.values.maxBasal} U/hr</div>
          <br />
        </div>
      );
    }
  }
  return <div></div>;
};

const InsulinModel = props => {
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>
          Insulin Model: {props.values.insulinModel === 'rapidAdult' ? 'Rapid Acting Adult' : 'Rapid Acting Child'}
        </div>
        <br />
      </div>
    );
  }
};

const SuspendThreshold = props => {
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Suspend Threshold: {props.values.suspendThreshold} mg/dL</div>
        <br />
      </div>
    );
  }
};

const CorrectionRange = props => {
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>Correction Ranges:</div>
        {props.values.correctionRange &&
          props.values.correctionRange.map((range, index) => (
            <div key={index} variant="subtitle1" color="textSecondary">
              {utils.fixTime(range.time)} : {range.lowerLimit} mg/dL - {range.upperLimit} mg/dL
            </div>
          ))}
        <br />
      </div>
    );
  }
};

const Training = props => {
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else if (props.values.training === 'in-person') {
    return (
      <div>
        <div>Training: In-person training required</div>
        <br />
      </div>
    );
  } else if (props.values.training === 'in-module') {
    return (
      <div>
        <div>Training: Online module training</div>
        <br />
      </div>
    );
  }
  return <div></div>;
};

const LoopMode = props => {
  if (props.values.loopMode === 'som') {
    return (
      <div>
        <div>Tidepool Loop Mode: Suspend-only</div>
        <br />
      </div>
    );
  } else if (props.values.loopMode === 'clm') {
    return (
      <div>
        <div>Tidepool Loop Mode: Closed-loop</div>
        <br />
      </div>
    );
  }
  return <div></div>;
};

const InitialTherapySetting = props => {
  if (props.values.therapySetting === 'initial') {
    return <div></div>;
  } else if (props.values.therapySetting === 'tps') {
    return (
      <div>
        <div>Initial Therapy Settings:</div>
        <div>
          {props.values.firstName + ' will transfer standard pump settings on Tidepool Loop download or training'}
        </div>
        <br />
      </div>
    );
  } else if (props.values.therapySetting === 'cpt') {
    return (
      <div>
        <div>Initial Therapy Settings:</div>
        <div>{'CPT will help determine ' + props.values.firstName + "'s settings"}</div>
        <br />
      </div>
    );
  }
  return <div></div>;
};

const Header = props => {
  var today = new Date();
  var months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  return (
    <div>
      <div>Patient: {props.values.firstName + ' ' + props.values.lastName}</div>
      <div>{props.values.birthday ? 'Date of Birth: ' + utils.birthdayFix(props.values.birthday) : ''}</div>
      <div>{props.values.yearOfDiagnosis ? 'Year of Diagnosis: ' + props.values.yearOfDiagnosis : ''}</div>
      <div>{'Date Copied: ' + months[today.getMonth()] + ' ' + today.getDate() + ', ' + today.getFullYear()}</div>
      <br />
    </div>
  );
};

/*
 *  CopyPaste is a component which prepares the patient information to be copy and pasted. It is hidden from the screen, and is not styled.
 */

const CopyPaste = props => {
  return (
    <div>
      <Header values={props.values} />
      <InitialTherapySetting values={props.values} />
      <LoopMode values={props.values} />
      <Training values={props.values} />
      <CorrectionRange values={props.values} />
      <SuspendThreshold values={props.values} />
      <InsulinModel values={props.values} />
      <MaxBasal values={props.values} />
      <MaxBolus values={props.values} />
      <BasalRates values={props.values} />
      <InsulinToCarbRatios values={props.values} />
      <SensitivityRates values={props.values} />
    </div>
  );
};

export default CopyPaste;
